/**
 * "2 h 14 min" in whatever language the interface is in.
 *
 * Not a catalog string with `{h}` and `{m}` in it: every language builds a
 * duration its own way, and gluing one together from pieces across 20
 * catalogs is 20 chances to get the order, the spacing or the unit wrong.
 * `Intl` already knows all of it. The catalog receives the finished text.
 */

export interface DurationParts {
  hours: number
  minutes: number
}

/** Whole hours and whole minutes, rounded DOWN — a progress line that says
 * "3 h 06 min of 3 h 05 min" has overtaken the file it is describing. */
export function splitDuration(seconds: number): DurationParts {
  const total = Math.max(0, Math.floor(seconds / 60))
  return { hours: Math.floor(total / 60), minutes: total % 60 }
}

/** Whole minutes, always rounded UP: "about 8 minutes" for 7 min 43 s. A
 * precision we do not have reads as a lie the moment the number misses. */
export function roundUpToMinute(seconds: number): number {
  return Math.max(1, Math.ceil(seconds / 60))
}

/** How wide to spell the units. The sentence ("about 2 minutes left") wants
 * words; the counter under it ("27 min of 1 hr") wants them out of the way. */
export type DurationStyle = 'short' | 'long'

interface DurationFormatCtor {
  new (
    locale: string,
    options: { style: string },
  ): { format(parts: { hours?: number; minutes?: number }): string }
}

export function formatDuration(
  seconds: number,
  locale: string,
  style: DurationStyle = 'short',
): string {
  const { hours, minutes } = splitDuration(seconds)
  // Intl.DurationFormat is the right tool and lands browser by browser; where
  // it is missing the units are formatted one at a time and joined, which is
  // what it does anyway for the two units we use.
  const DurationFormat = (Intl as unknown as { DurationFormat?: DurationFormatCtor }).DurationFormat
  if (DurationFormat) {
    try {
      const out = new DurationFormat(locale, { style }).format(
        hours > 0 ? { hours, minutes } : { minutes },
      )
      // A zero duration comes back as the empty string — it drops values it
      // considers absent — and "of 21m transcribed" is missing its subject.
      if (out.trim()) return out
    } catch {
      // fall through to the units below
    }
  }
  const parts: string[] = []
  if (hours > 0) parts.push(unit(hours, 'hour', locale, style))
  if (hours === 0 || minutes > 0) parts.push(unit(minutes, 'minute', locale, style))
  return parts.join(' ')
}

function unit(
  value: number,
  name: 'hour' | 'minute',
  locale: string,
  style: DurationStyle,
): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'unit',
      unit: name,
      unitDisplay: style,
    }).format(value)
  } catch {
    // An engine without the unit style still has to say something
    return `${value}${name === 'hour' ? 'h' : 'm'}`
  }
}
